import { io } from "socket.io-client";

let socket = null;
let messageQueue = [];
let reconnectAttempts = 0;
let isReconnecting = false;
let manuallyDisconnected = false;
let lastUpdateTime = 0;
let pendingUpdate = null;
let updateTimer = null;

// Initialize socket connection
export const initSocket = () => {
  if (!socket) {
    // Use the API base URL from environment or default to localhost
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    // For socket connection, we need to use the base URL without the /api path
    const socketUrl = baseUrl.endsWith("/api")
      ? baseUrl.substring(0, baseUrl.length - 4) // Remove /api if present
      : baseUrl;

    console.log("[SOCKET] Initializing socket connection to:", socketUrl);

    socket = io(socketUrl, {
      transports: ["websocket", "polling"], // Fallback to polling if websocket fails
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000, // Increase timeout to 20 seconds
    });

    socket.on("connect", () => {
      console.log("[SOCKET] Connected to socket server");
      reconnectAttempts = 0;
      isReconnecting = false;

      // Process any queued messages
      if (messageQueue.length > 0) {
        console.log(
          `[SOCKET] Processing ${messageQueue.length} queued messages`
        );
        processMessageQueue();
      }
    });

    socket.on("connect_error", (error) => {
      console.error("[SOCKET] Connection error:", error.message);
      if (!isReconnecting) {
        isReconnecting = true;
        reconnectAttempts++;
        console.log(`[SOCKET] Reconnection attempt ${reconnectAttempts}`);
      }
    });

    socket.on("disconnect", (reason) => {
      console.log("[SOCKET] Disconnected from socket server:", reason);

      // If the disconnection isn't manual, try to reconnect
      if (!manuallyDisconnected && reason !== "io client disconnect") {
        console.log("[SOCKET] Will attempt to reconnect automatically");
        isReconnecting = true;
      }
    });

    socket.on("reconnect", (attemptNumber) => {
      console.log(`[SOCKET] Reconnected after ${attemptNumber} attempts`);
      isReconnecting = false;
    });

    socket.on("reconnect_error", (error) => {
      console.error("[SOCKET] Reconnection error:", error.message);
      isReconnecting = true;
    });

    socket.on("reconnect_failed", () => {
      console.error("[SOCKET] Reconnection failed after all attempts");
      isReconnecting = false;
    });
  }

  return socket;
};

// Process queued messages
const processMessageQueue = () => {
  if (!socket || !socket.connected || messageQueue.length === 0) return;

  // Process all queued messages
  while (messageQueue.length > 0) {
    const { event, payload } = messageQueue.shift();
    console.log(`[SOCKET] Sending queued message: ${event}`);
    socket.emit(event, payload);
  }
};

// Get the socket instance (initialize if needed)
export const getSocket = () => {
  if (!socket) {
    return initSocket();
  }
  return socket;
};

// Get socket connection status
export const getSocketStatus = () => {
  if (!socket) return "disconnected";
  return socket.connected ? "connected" : "disconnected";
};

// Queue a message to be sent when connection is available
export const queueMessage = (event, payload) => {
  messageQueue.push({ event, payload });
  console.log(
    `[SOCKET] Message queued: ${event}, queue size: ${messageQueue.length}`
  );

  // If we're connected, try to process the queue immediately
  if (socket && socket.connected) {
    processMessageQueue();
  } else {
    // If disconnected but not reconnecting, try to reconnect
    if (
      socket &&
      !socket.connected &&
      !isReconnecting &&
      !manuallyDisconnected
    ) {
      console.log("[SOCKET] Attempting to reconnect...");
      socket.connect();
    }
  }
};

// Join a meeting room for collaborative editing
export const joinMeeting = (meetingId, user, isReconnect = false) => {
  if (!meetingId || !user) {
    console.error("[SOCKET] Cannot join meeting: missing meetingId or user");
    return;
  }

  const socket = getSocket();
  const payload = {
    meetingId,
    user: {
      userId: user.id || user._id,
      name: user.name || "Anonymous",
    },
    isReconnect,
  };

  if (socket && socket.connected) {
    socket.emit("join-meeting", payload);
    console.log(`[SOCKET] Joined meeting room: ${meetingId}`);
  } else {
    console.warn(
      "[SOCKET] Socket not connected, queueing join-meeting message"
    );
    queueMessage("join-meeting", payload);

    // Try to reconnect if we're not already reconnecting
    if (socket && !isReconnecting && !manuallyDisconnected) {
      console.log("[SOCKET] Triggering reconnection attempt");
      socket.connect();
    }
  }
};

// Leave a meeting room
export const leaveMeeting = (meetingId, userId) => {
  if (!meetingId || !userId) {
    console.log("[SOCKET] Cannot leave meeting: missing meetingId or userId");
    return;
  }

  const socket = getSocket();
  const payload = {
    meetingId,
    userId,
  };

  if (socket && socket.connected) {
    socket.emit("leave-meeting", payload);
    console.log(`[SOCKET] Left meeting room: ${meetingId}`);
  } else {
    console.log(
      `[SOCKET] Cannot leave meeting ${meetingId}: socket not connected, queueing message`
    );
    queueMessage("leave-meeting", payload);
  }
};

// Send notes update to other users
export const sendNotesUpdate = (meetingId, data) => {
  if (!meetingId || !data) {
    console.error("[SOCKET] Cannot send update: missing meetingId or data");
    return false;
  }

  const socket = getSocket();

  // Prepare data payload
  const payload = {
    meetingId,
    content: data.content,
    userId: data.userId || "anonymous",
    userName: data.userName || "Anonymous",
    timestamp: data.timestamp || new Date(),
  };

  // Clear any pending update timeout
  if (updateTimer) {
    clearTimeout(updateTimer);
    updateTimer = null;
  }

  // Store this as the pending update
  pendingUpdate = payload;

  // Check if we've sent an update recently
  const now = Date.now();
  const timeSinceLastUpdate = now - lastUpdateTime;

  // If we've sent an update within the last 800ms, debounce this update
  if (timeSinceLastUpdate < 800) {
    console.log(
      `[SOCKET] Debouncing update, last update was ${timeSinceLastUpdate}ms ago`
    );

    // Schedule an update after the debounce period
    updateTimer = setTimeout(() => {
      if (pendingUpdate) {
        console.log("[SOCKET] Sending debounced update");
        sendDebouncedUpdate(pendingUpdate);
        pendingUpdate = null;
      }
    }, 800 - timeSinceLastUpdate);

    return true;
  }

  // Not debounced, send immediately
  return sendDebouncedUpdate(payload);
};

// Helper function to actually send the update
const sendDebouncedUpdate = (payload) => {
  const socket = getSocket();

  if (socket && socket.connected) {
    console.log(
      "[SOCKET] Sending notes update to server:",
      payload.content.substring(0, 50) + "..."
    );

    // Update the last update time
    lastUpdateTime = Date.now();

    // Emit the update event
    socket.emit("notes-update", payload);
    return true;
  } else {
    console.warn(
      "[SOCKET] Socket not connected, queueing notes-update message"
    );
    queueMessage("notes-update", payload);

    // Try to reconnect if we're not already reconnecting
    if (socket && !isReconnecting && !manuallyDisconnected) {
      console.log("[SOCKET] Triggering reconnection attempt for notes update");
      socket.connect();
    }
    return false;
  }
};

// Clean up any pending updates
export const clearPendingUpdates = () => {
  if (updateTimer) {
    clearTimeout(updateTimer);
    updateTimer = null;
  }
  pendingUpdate = null;
};

// Force reconnection attempt
export const reconnectSocket = () => {
  if (socket) {
    console.log("[SOCKET] Forcing reconnection attempt");
    manuallyDisconnected = false;
    socket.connect();
    return true;
  }
  return false;
};

// Disconnect socket
export const disconnectSocket = () => {
  if (socket) {
    // Clean up any pending updates
    clearPendingUpdates();

    manuallyDisconnected = true;
    socket.disconnect();
    console.log("[SOCKET] Socket manually disconnected");
    return true;
  }
  return false;
};

// Clear message queue
export const clearMessageQueue = () => {
  const queueSize = messageQueue.length;
  messageQueue = [];
  console.log(`[SOCKET] Message queue cleared, ${queueSize} messages removed`);
  return queueSize;
};
